import { createContext, useContext, useState, useEffect } from 'react';

export interface IndustryLabel {
  id: string;
  industry: string;
  label_type: 'group' | 'unit' | 'site' | 'team';
  label_value: string;
  icon?: string | null; // Lucide-react icon name
  superadmin_override?: boolean;
  admin_override?: boolean;
}

export interface SystemConfig {
  autoHideIndustryFilter: boolean;
  defaultHomePage: 'dashboard' | 'map';
}

export const DEFAULT_SYSTEM_CONFIG: SystemConfig = {
  autoHideIndustryFilter: true, // Default: auto-hide when only 1 industry
  defaultHomePage: 'map', // Default: Map view instead of Dashboard
};

// Types for exposing RBAC data (matches RBACContext)
export interface AdminUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_internal: boolean;
  default_company_id?: string;
  default_group_id?: string;
}

export interface AdminCompany {
  id: string;
  name: string;
  slug: string;
  is_internal: boolean;
  role: string;
  industry?: string;
  logo_url?: string | null;
  theme_json?: {
    logo_light_url?: string | null;
    logo_dark_url?: string | null;
    icon_light_url?: string | null;
    icon_dark_url?: string | null;
    use_custom_logo?: boolean;
    logo_display_mode?: 'replace' | 'collab';
    use_single_logo?: boolean;
    use_single_icon?: boolean;
  } | null;
}

export interface AdminMembership {
  id: string;
  company_id: string;
  group_id: string | null;
  role: string;
  company_name: string;
  group_name?: string;
}

export interface SettingsContextType {
  // RBAC data (from RBACContext - single source of truth)
  user: AdminUser | null;
  companies: AdminCompany[];
  memberships: AdminMembership[];
  permissions: string[];
  // T-068 P3: server-computed feature-visibility flags (mirrors RBACContext).
  capabilities: { report_subscriptions_admin: boolean; unbilled_deployments_view: boolean };
  is_superadmin: boolean;

  // Settings-specific data (managed here)
  labels: IndustryLabel[];
  systemConfig: SystemConfig;
  loading: boolean;

  // Actions
  refresh: () => Promise<void>;
  refreshLabels: () => Promise<void>;
  updateSystemConfig: (updates: Partial<SystemConfig>) => void;
}

export const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SYSTEM_CONFIG_STORAGE_KEY = 'ht_system_config';

// Load system config from localStorage (with defaults)
export function loadSystemConfig(): SystemConfig {
  try {
    const stored = localStorage.getItem(SYSTEM_CONFIG_STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SYSTEM_CONFIG, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.warn('Failed to load system config from localStorage:', error);
  }
  return DEFAULT_SYSTEM_CONFIG;
}

// Save system config to localStorage
export function saveSystemConfig(config: SystemConfig): void {
  try {
    localStorage.setItem(SYSTEM_CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.warn('Failed to save system config to localStorage:', error);
  }
}

export function useSettingsContext() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettingsContext must be used within SettingsProvider');
  }
  return context;
}

/**
 * Hook to access system config without requiring SettingsProvider.
 * Safe to use anywhere in the app (Dashboard, Map, etc.)
 */
export function useSystemConfig() {
  const [config, setConfig] = useState<SystemConfig>(loadSystemConfig);

  const updateConfig = (updates: Partial<SystemConfig>) => {
    setConfig((prev) => {
      const newConfig = { ...prev, ...updates };
      saveSystemConfig(newConfig);
      return newConfig;
    });
  };

  // Listen for storage events to sync across tabs
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === SYSTEM_CONFIG_STORAGE_KEY && e.newValue) {
        try {
          setConfig({ ...DEFAULT_SYSTEM_CONFIG, ...JSON.parse(e.newValue) });
        } catch {
          // Ignore parse errors
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return { systemConfig: config, updateSystemConfig: updateConfig };
}
